import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from '@tanstack/react-router'
import { getClaimToneClassName } from '@/components/molecules'
import { usePetClaim } from '@/shared/hooks/queries/use-pet-claim'
import { usePetPolicyIds } from '@/shared/hooks/queries/use-pet-policy-ids'
import { usePetClaimDocuments } from '@/shared/hooks/queries/use-pet-claim-documents'
import { usePets } from '@/shared/hooks/queries/use-pets'
import { usePolicies } from '@/shared/hooks/queries/use-policies'
import { useUser } from '@/shared/hooks/use-user'
import { getJwt } from '@/shared/lib/betterAuth/get-jwt'
import { downloadPetClaimDocument } from '@/services/claims.services'
import { formatCurrency } from '@/shared/helpers/format-currency'
import { saveBlob } from '@/shared/helpers/save-blob'
import { formatDate, parseApiDate } from '@/shared/helpers/format-date'
import {
  CENTS_PER_DOLLAR,
  MISSING_FIELDS_SEPARATOR,
} from '@/shared/constants/claims'
import { ROUTES } from '@/shared/constants/routes'
import { PET_CLAIM_STATUS } from '@/shared/types/global.types'
import type { ClaimDocument } from '@/shared/types/global.types'
import {
  getClaimStatusKey,
  getClaimStatusTone,
  isClaimEditable,
} from '@/shared/helpers/claims-status'
import { buildPetClaimCard } from '@/shared/helpers/pet-claim-card'
import { useSubmitClaimModal } from '@/shared/hooks/use-submit-claim-modal'
import { useClaimDocumentPreview } from '@/shared/hooks/use-claim-document-preview'

type DetailRow = { label: string; value: string }

export function useClaimsDetailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { claimId = '' } = useParams({ strict: false })
  const { user } = useUser()
  const { policyIds, isLoading: isPolicyIdsLoading } = usePetPolicyIds()
  const claimQuery = usePetClaim(claimId, policyIds)
  const documentsQuery = usePetClaimDocuments(claimId)
  const { data: policies = [] } = usePolicies()
  const { data: pets = [] } = usePets()
  const [downloadingId, setDownloadingId] = useState<string | undefined>(
    undefined,
  )
  const [hasDownloadError, setHasDownloadError] = useState(false)
  const documentPreview = useClaimDocumentPreview(claimId)

  const claim = claimQuery.data

  const view = useMemo(() => {
    if (!claim) return undefined
    const rows: DetailRow[] = [
      {
        label: t('claims.detail.invoiceDate'),
        value: formatDate(parseApiDate(claim.invoice_date)),
      },
      {
        label: t('claims.detail.createdAt'),
        value: formatDate(claim.created_at),
      },
      {
        label: t('claims.detail.filedAt'),
        value: formatDate(claim.filed_at),
      },
    ]
    return {
      claimNumber: claim.core_claim_id ? `#${claim.core_claim_id}` : '',
      statusLabel: t(getClaimStatusKey(claim.status), {
        defaultValue: claim.status,
      }),
      statusTone: getClaimStatusTone(claim.status),
      missingFieldsMessage: claim.missing_fields.length
        ? t('claims.detail.missingFields', {
            fields: claim.missing_fields
              .map((field) => field.label)
              .join(MISSING_FIELDS_SEPARATOR),
          })
        : '',
      // The API sends null for fields not filled in yet
      amount:
        typeof claim.amount === 'number'
          ? formatCurrency(claim.amount / CENTS_PER_DOLLAR)
          : '',
      description: claim.description ?? '',
      rows: rows.filter((row) => !!row.value),
    }
  }, [claim, t])

  const documents = (documentsQuery.data ?? []).map((document) => ({
    ...document,
    typeLabel: t(`claims.detail.documentTypes.${document.type}`, {
      defaultValue: '',
    }),
  }))

  // The claim only carries its policy number; the pet comes from that policy
  const policy = policies.find(
    (item) => String(item.policyExternalId) === String(claim?.policy_id),
  )
  const pet = pets.find((item) => item.id === policy?.petId)

  // Downloads need the server-side key, so the file comes through the proxy
  // and is handed to the browser as a blob
  async function handleDownload(document: ClaimDocument) {
    setDownloadingId(document.id)
    setHasDownloadError(false)
    try {
      const token = await getJwt()
      if (!token) throw new Error('No JWT token')
      const blob = await downloadPetClaimDocument(token, claimId, document.id)
      saveBlob(blob, document.file_name)
    } catch (error) {
      console.error(error)
      setHasDownloadError(true)
    } finally {
      setDownloadingId(undefined)
    }
  }

  function handleEdit() {
    navigate({ to: ROUTES.CLAIM_EDIT, params: { claimId } })
  }

  // Cancel keeps the user on the draft; once submitted it leaves the drafts
  const submitModal = useSubmitClaimModal({
    onCancel: () => undefined,
    onFinish: () => navigate({ to: ROUTES.CLAIMS }),
  })

  function handleOpenSubmit() {
    if (claim) submitModal.open(claim)
  }

  const isLoading = isPolicyIdsLoading || claimQuery.isLoading

  return {
    view,
    card: buildPetClaimCard(t, pet, policy, user?.fullName ?? ''),
    documents,
    isDocumentsLoading: documentsQuery.isLoading,
    downloadingId,
    hasDownloadError,
    handleDownload,
    documentPreview,
    canEdit: isClaimEditable(claim?.status),
    canSubmit: claim?.status === PET_CLAIM_STATUS.FILEABLE,
    handleEdit,
    submitModal,
    handleOpenSubmit,
    statusClassName: view ? getClaimToneClassName(view.statusTone) : '',
    isLoading,
    isNotFound: !isLoading && !view,
  }
}
